import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Landmark,
  ShieldCheck,
  FileText,
  Scale,
  Building2,
  Flag,
  Users,
  Receipt,
  ClipboardCheck,
  MapPin,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Landmark,
    title: 'Government procurement cycle',
    detail:
      '12-24 months is the typical end-to-end window for a public-sector AP modernization procurement — from initial requirements through RFI, RFP, evaluation, award, contract, and kickoff. The December 2026 12c Premier Support end and December 2027 Extended Support end mean the procurement clock is already running for agencies that have not started.',
  },
  {
    icon: ShieldCheck,
    title: 'Compliance posture, stated honestly',
    detail:
      'EZ Cloud is not currently FedRAMP authorized. For agencies that strictly require FedRAMP, the deployment path is Oracle Cloud Infrastructure Government (OC2 / OC3) with a FedRAMP-authorized integrator. For agencies that do not strictly require FedRAMP, OCI commercial regions provide SOC 2 Type II and in-region data residency.',
  },
  {
    icon: FileText,
    title: 'Audit trail preservation',
    detail:
      'Public-sector AP requires an end-to-end audit trail: who approved what, when, against which fund, against which grant, with which supporting document attached. WebCenter\'s existing audit history is preserved through modernization and surfaced into the modern AP workflow.',
  },
  {
    icon: Scale,
    title: 'Procurement transparency',
    detail:
      'State and municipal transparency laws often require visible procurement and disbursement data — searchable, downloadable, machine-readable. AP modernization for public sector needs to integrate cleanly with the agency\'s transparency portal, not work against it.',
  },
];

const WHERE_CUSTOMERS_ARE = [
  {
    icon: Flag,
    title: 'Federal agencies on EBS R12 or PeopleSoft',
    detail:
      'Federal agencies typically run Oracle E-Business Suite R12 or PeopleSoft Financials as the system of record, with WebCenter Imaging and Forms Recognition as the AP capture layer. Many were stood up a decade or more ago. The ERP modernization roadmap and the WebCenter modernization roadmap are usually two different conversations with two different sponsors.',
  },
  {
    icon: MapPin,
    title: 'State agencies on EBS or PeopleSoft',
    detail:
      'State Departments of Revenue, Transportation, Health and Human Services, and other state agencies commonly run EBS R12 or PeopleSoft on top of WebCenter for AP imaging. State-level data residency requirements and state-specific transparency laws shape the modernization options.',
  },
  {
    icon: Building2,
    title: 'Municipalities on EBS, JD Edwards EnterpriseOne, or PeopleSoft',
    detail:
      'City and county governments run a wider mix — EBS, JD Edwards EnterpriseOne, or PeopleSoft Financials — with WebCenter providing AP imaging on top. Municipal AP teams are often smaller, and the WebCenter heritage is doing more lifting per person than the team size suggests.',
  },
  {
    icon: Users,
    title: 'The modernization layer that does not always get modernized',
    detail:
      'When the ERP modernizes — for example, EBS R12 to Fusion Cloud, or PeopleSoft to a target platform — the WebCenter AP layer underneath does not always come along on the same timeline. The result is a WebCenter AP implementation that has outlived the ERP project that originally deployed it.',
  },
];

const PUBLIC_SECTOR_REQUIREMENTS = [
  {
    icon: ClipboardCheck,
    title: 'Vendor master with disadvantaged business status',
    detail:
      'Public-sector vendor masters carry disadvantaged business status flags — small business, woman-owned, minority-owned, veteran-owned, HUBZone, 8(a), and equivalent state-level designations. Spend allocation against these categories is reported, often publicly. The modernized AP layer needs to read and preserve these flags through the full invoice lifecycle.',
  },
  {
    icon: Scale,
    title: 'Transparency reporting',
    detail:
      'Many states and municipalities operate transparency portals that publish vendor payments. The AP modernization needs to surface clean, structured payment data — vendor, amount, fund, date, purpose — for the transparency feed without requiring a separate manual export step.',
  },
  {
    icon: Receipt,
    title: 'Per-grant tracking on federal pass-through funds',
    detail:
      'When a state or municipal agency spends federal pass-through grant funds, each invoice line needs to carry the grant identifier through to the GL. Modern AP coding needs to support per-grant accounting fields — CFDA number, grant ID, period of performance, federal match status — so grant reporting is a query, not a reconstruction project.',
  },
  {
    icon: FileText,
    title: 'Multi-fund coding & encumbrance accounting',
    detail:
      'Public-sector chart of accounts is multi-fund by nature — general fund, special revenue funds, capital projects funds, debt service funds, enterprise funds. AP coding needs to respect fund boundaries, and the system needs to honor existing encumbrances so a paid invoice draws against the right purchase order encumbrance line.',
  },
  {
    icon: ShieldCheck,
    title: 'Prompt-Payment-Act compliance',
    detail:
      'The federal Prompt Payment Act, and equivalent state and municipal prompt-payment statutes, set interest-bearing payment deadlines. The modern AP workflow needs to surface payment due dates, calculate interest where applicable, and provide the audit evidence that supports prompt-payment compliance reporting.',
  },
];

const TRACK_RECORD = [
  {
    name: 'US House of Representatives',
    detail:
      'Federal legislative branch WebCenter engagement. Part of Andrew\'s direct delivery history across the Oracle WebCenter stack.',
  },
  {
    name: 'US Department of Agriculture',
    detail:
      'Federal cabinet department WebCenter engagement. Public-sector compliance context, federal procurement environment, federal grant pass-through.',
  },
  {
    name: 'State of Alaska',
    detail:
      'State government WebCenter engagement. State-level data residency, state procurement environment, multi-fund accounting context.',
  },
];

const PROCUREMENT_TIMELINE = [
  {
    step: '1',
    title: 'Pre-procurement discovery (now through Q3 2026)',
    body: 'Internal stakeholder alignment between IT, AP, procurement, and the agency CFO\'s office. Inventory of the existing WebCenter implementation, customizations, integrations, and audit trail requirements. The output is a clear statement of the forward path the agency wants to evaluate.',
  },
  {
    step: '2',
    title: 'RFI / sources sought (Q3-Q4 2026)',
    body: 'Market research instrument — RFI, Sources Sought notice, or equivalent state/municipal mechanism. Surfaces the qualified vendor pool, deployment-pattern options (OCI commercial vs. OCI Government), and compliance posture of each candidate against the agency\'s specific requirements.',
  },
  {
    step: '3',
    title: 'RFP issuance and evaluation (Q4 2026 - Q2 2027)',
    body: 'Formal RFP issued. Evaluation against published criteria — typically a weighted scoring rubric covering technical approach, compliance posture, references, corporate experience, and price. Award and contract negotiation follow.',
  },
  {
    step: '4',
    title: 'Award, contract, and kickoff (Q2-Q3 2027)',
    body: 'Contract execution, agency security review, integration kickoff. For agencies that started the procurement at the right point in 2026, the cutover lands inside the 12c Extended Support window (which ends December 2027). For agencies that start later, the cutover lands after Extended Support has ended.',
  },
];

const COMPLIANCE_ROWS = [
  {
    dimension: 'FedRAMP',
    status:
      'EZ Cloud is not currently FedRAMP authorized. For FedRAMP-required workloads, deployment is on Oracle Cloud Infrastructure Government (OC2 / OC3) with a FedRAMP-authorized integrator.',
  },
  {
    dimension: 'OCI Government (OC2 / OC3)',
    status:
      'Oracle Cloud Infrastructure Government regions are FedRAMP High authorized (OC2) and DoD IL5 / IL6 authorized (OC3). For agencies that require these baselines, the WebCenter modernization deployment pattern targets these regions.',
  },
  {
    dimension: 'OCI commercial regions',
    status:
      'OCI commercial regions provide SOC 2 Type II and in-region data residency. For state, municipal, and federal workloads that do not strictly require FedRAMP, OCI commercial is the typical deployment.',
  },
  {
    dimension: 'In-region data residency',
    status:
      'Deployment region is selected to match the agency\'s state-level or federal data-residency requirements. EZ Cloud does not move customer data out of the deployment region.',
  },
  {
    dimension: 'Audit trail preservation',
    status:
      'WebCenter\'s existing audit history — who approved what, when, against which fund — is preserved through modernization and surfaced into the modern AP workflow. Audit evidence is retrievable for the retention period the agency requires.',
  },
  {
    dimension: 'SOC 2 Type II',
    status:
      'EZ Cloud operations are aligned to SOC 2 Type II controls. Specific attestation status is provided during procurement.',
  },
];

const SCENARIOS = [
  {
    icon: MapPin,
    title: 'State agency mid-modernization',
    body: 'The agency is mid-way through a multi-year ERP modernization — EBS R12 to Fusion, or PeopleSoft to a target — and the WebCenter AP layer underneath has not been decided. The December 2026 cliff forces a decision in parallel with the ERP modernization, not after it. Sequencing the two roadmaps so they meet at cutover, rather than colliding mid-project, is the conversation.',
  },
  {
    icon: Flag,
    title: 'Federal agency wanting WebCenter on OC2',
    body: 'A federal civilian agency on EBS R12 + WebCenter wants the WebCenter modernization deployed on Oracle Cloud Infrastructure Government (OC2) to inherit the FedRAMP High authorization boundary. The agency needs a deployment pattern that matches the OC2 architectural constraints and a delivery partner that can operate in that environment.',
  },
  {
    icon: Building2,
    title: 'Municipality on EBS R12 considering Fusion',
    body: 'A city or county finance department is on EBS R12 with WebCenter AP imaging on top. The municipality is weighing EBS R12 forward versus a Fusion migration, and the WebCenter AP layer needs a forward path that does not pre-commit either ERP decision. AP modernization that decouples cleanly from the ERP decision is the requirement.',
  },
  {
    icon: Receipt,
    title: 'Pass-through grant tracking requirement',
    body: 'A state or municipal agency receives federal pass-through grant funds and is required to report per-grant spend back to the federal funder. The current AP workflow does not carry the grant identifier all the way to the GL, and the reconstruction is a manual reporting project every quarter. Per-grant coding at invoice capture is the unlock.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    tag: 'Tier 1 · Public-Sector Decision Call',
    title: 'No-fee 30-minute decision call',
    body: 'A 30-minute call with Andrew to walk through the agency\'s WebCenter implementation, the procurement cycle considerations, and the compliance posture options. The output is a clearer statement of what the agency is actually evaluating and which forward path is the right fit.',
  },
  {
    tag: 'Tier 2 · Public-Sector Assessment',
    title: 'Fixed-fee modernization assessment',
    body: 'Andrew reads the existing WebCenter implementation directly and delivers a modernization plan tailored to the public-sector context — compliance posture, audit trail preservation, transparency reporting integration, grant tracking, multi-fund coding, and procurement cycle alignment. The plan is yours to keep regardless of which delivery partner you choose.',
  },
  {
    tag: 'Tier 3 · Standard Consulting Delivery',
    title: 'Time-and-materials or fixed-fee delivery',
    body: 'Standard consulting delivery for the modernization — discovery, design, build, UAT, cutover. Suitable for agencies on contract vehicles that accept conventional T&M or fixed-fee SOWs. Engagement scope is shaped by the agency\'s contract vehicle and award structure.',
  },
  {
    tag: 'Tier 4 · Forward Deployed Engineering',
    title: 'Embedded delivery model',
    body: 'A founder-led embedded engineering engagement for agencies that need closer, faster iteration during the modernization. FDE is the right mode where requirements are evolving — for example, where transparency reporting integration or per-grant coding is being designed during delivery rather than fully specified up front.',
  },
];

const FAQS = [
  {
    q: 'Is EZ Cloud FedRAMP authorized?',
    a: 'EZ Cloud is not currently FedRAMP authorized. For agencies that strictly require FedRAMP for the AP workload, the deployment path is Oracle Cloud Infrastructure Government (OC2 or OC3) with a FedRAMP-authorized integrator on the contract vehicle. For agencies that do not strictly require FedRAMP — many state, municipal, and federal civilian workloads — OCI commercial regions provide SOC 2 Type II and in-region data residency. We will tell you honestly during a Decision Call whether your specific compliance posture is a fit.',
  },
  {
    q: 'Can the modernization be deployed on OCI Government (OC2 / OC3)?',
    a: 'Yes — OCI Government regions are the appropriate target for FedRAMP High (OC2) and DoD IL5 / IL6 (OC3) workloads. The WebCenter modernization deployment pattern targets these regions when the agency\'s compliance baseline requires it. Because EZ Cloud is not itself FedRAMP authorized, the contract structure typically involves a FedRAMP-authorized integrator as the prime, with EZ Cloud delivering the WebCenter expertise. The right structure depends on the agency\'s contract vehicle.',
  },
  {
    q: 'What about state-level data residency?',
    a: 'The deployment region is selected to match the agency\'s state-level data-residency requirements. OCI has commercial regions across the US and internationally, and OCI Government regions for US federal and US public-sector workloads. EZ Cloud does not move customer data out of the deployment region. State-specific residency clauses in the agency\'s contract are honored at the deployment layer, not as an after-the-fact addendum.',
  },
  {
    q: 'How does the modernization integrate with transparency reporting?',
    a: 'The modernized AP workflow surfaces clean structured payment data — including disadvantaged business status flags where the agency reports against them — into the agency\'s existing transparency feed. State and municipal transparency portals typically pull vendor, amount, fund, date, and purpose fields from the AP and GL systems on a schedule. Integration is on the agency\'s schedule and format, not the vendor\'s. We do not stand in front of the agency\'s transparency portal.',
  },
  {
    q: 'How does this support Prompt-Payment-Act compliance?',
    a: 'The modernized AP workflow surfaces invoice receipt date, payment due date, and the audit evidence that supports prompt-payment reporting under the federal Prompt Payment Act and equivalent state and municipal statutes. These statutes set interest-bearing payment deadlines, and the workflow distinguishes — for example — when a discrepancy is the cause of a payment delay versus an internal workflow delay. Interest calculation is supported where the agency operates that scheme.',
  },
  {
    q: 'How does per-grant tracking work on federal pass-through funds?',
    a: 'Grant identifiers are captured at invoice coding and carried through to the GL. The relevant fields — CFDA number, grant ID, period of performance, federal match status, vendor disadvantaged business status — are coded once at the AP step rather than reconstructed during quarterly grant reporting. Coding rules are configured to the agency\'s chart of accounts and grant reporting schema, not a vendor-standard template.',
  },
  {
    q: 'What procurement contract vehicles are available?',
    a: 'EZ Cloud engages through agency-direct contracts, state cooperative purchasing schedules, and partnerships with prime contractors on federal and state vehicles. The right vehicle depends on the agency. For FedRAMP-required federal workloads, the contract structure typically involves a FedRAMP-authorized prime. For state and municipal workloads, the available vehicles vary by jurisdiction. We will work through the right structure during the procurement phase.',
  },
  {
    q: 'What is the typical timeline from RFP to cutover?',
    a: 'Government procurement runs 12-24 months from RFP issuance to project kickoff, and the modernization delivery is typically 4-9 months after kickoff depending on scope and compliance review cycles. Agencies that start the RFP process in Q3-Q4 2026 land their cutover inside the 12c Extended Support window (which ends December 2027). Agencies that start later land their cutover after Extended Support has ended and are operating on Sustaining Support until cutover.',
  },
];

const WebCenterGovernmentPublicSector = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter for Government & Public Sector — AP Modernization with Compliance Posture | EZ Cloud',
    description:
      'Oracle WebCenter AP modernization for state agencies, federal agencies, and municipalities. FedRAMP-relevant deployment patterns, in-region residency, audit trail preservation, and procurement transparency. The December 2026 12c support cliff and the government-procurement-cycle planning horizon.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-government-public-sector',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter Government' },
      { '@type': 'Thing', name: 'Public Sector AP Automation' },
      { '@type': 'Thing', name: 'FedRAMP' },
      { '@type': 'Thing', name: 'Oracle Cloud Infrastructure Government' },
      { '@type': 'Thing', name: 'State Government' },
      { '@type': 'Thing', name: 'Federal Agency' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter for Government & Public Sector — AP Modernization with Compliance Posture | EZ Cloud"
        description="Oracle WebCenter AP modernization for state agencies, federal agencies, and municipalities. FedRAMP-relevant deployment patterns, in-region residency, audit trail preservation, and procurement transparency. The December 2026 12c support cliff and the government-procurement-cycle planning horizon."
        canonical="https://ezcloud.ai/solutions/webcenter-government-public-sector"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter for Government & Public Sector',
            url: 'https://ezcloud.ai/solutions/webcenter-government-public-sector',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Landmark className="w-4 h-4" aria-hidden />
              Public Sector · Government · Oracle WebCenter AP
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter for government & public sector
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Compliance posture. Audit trail. Procurement-cycle planning.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              AP modernization for state, federal, and municipal Oracle WebCenter customers.
              Compliance posture, audit trail preservation, transparency reporting. Government
              procurement cycle planning for the December 2026 forward path.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-government-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Public-Sector Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              WebCenter AP modernization for government, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter AP modernization for state, federal, and municipal agencies
                  built on the public-sector compliance baseline — audit trail preservation,
                  in-region data residency, procurement transparency, prompt-payment compliance,
                  multi-fund coding, and per-grant tracking on federal pass-through funds.
                </strong>{' '}
                The deployment target is Oracle Cloud Infrastructure — commercial regions for
                workloads that do not strictly require FedRAMP, OCI Government (OC2 / OC3) for
                FedRAMP-required workloads delivered through a FedRAMP-authorized prime.
              </p>
              <p>
                EZ Cloud is not currently FedRAMP authorized. We say that on the front page of any
                public-sector conversation rather than in a procurement footnote. For agencies that
                strictly require FedRAMP, the contract structure involves a FedRAMP-authorized
                integrator. For agencies that do not strictly require FedRAMP, OCI commercial
                regions provide SOC 2 Type II and in-region residency.
              </p>
              <p>
                Government procurement runs 12-24 months. The December 2026 Premier Support end and
                the December 2027 Extended Support end on WebCenter 12c mean the procurement clock
                is already running. Agencies that start the RFP process in Q3-Q4 2026 land their
                cutover inside the Extended Support window. Agencies that start later land cutover
                after Extended Support has ended.
              </p>
            </div>
          </div>
        </section>

        {/* Key facts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The four facts to know
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Public-sector WebCenter modernization in four facts
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Procurement cycle, compliance posture, audit trail, and transparency — the four
              constraints that shape every public-sector AP modernization conversation.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Where government WebCenter customers are today */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Where customers are today
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where government WebCenter customers are today
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              State and federal agencies on EBS R12 and PeopleSoft. Municipalities on EBS, JD
              Edwards EnterpriseOne, or PeopleSoft Financials. WebCenter AP is the modernization
              layer that does not always get modernized when the ERP does.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHERE_CUSTOMERS_ARE.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Public-sector AP-specific requirements */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Public-sector AP requirements
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The AP requirements that are specifically public sector
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Public-sector AP carries a set of requirements that private-sector AP automation
              platforms rarely treat as first-class. Modernization needs to honor each of them at
              the data model, not as a reporting afterthought.
            </p>
            <div className="space-y-5">
              {PUBLIC_SECTOR_REQUIREMENTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                      <p className="text-gray-700 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Government track record */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Government track record
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Andrew has delivered WebCenter inside government before
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              From the founder bio — direct WebCenter delivery history across federal legislative,
              federal executive, and state-government engagements. Public-sector context is not
              theoretical here.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {TRACK_RECORD.map((t) => (
                <Card key={t.name} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                    <Flag className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{t.detail}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Full delivery history on the{' '}
              <Link to="/about" className="text-accent font-semibold hover:underline">
                founder's About page
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Procurement cycle implications */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Procurement cycle
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The 12-24 month procurement clock is already running
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Government procurement runs 12-24 months from initial discovery to project kickoff.
              The December 2026 12c Premier Support end means agencies need to start procurement
              now to have a forward path in place. Each step has its own internal alignment work
              before the next can start.
            </p>
            <div className="space-y-5">
              {PROCUREMENT_TIMELINE.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the full 12c end-of-support decision frame
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* FedRAMP & compliance posture */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Compliance posture — stated honestly
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              FedRAMP, OCI Government, and the deployment patterns
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              We say the FedRAMP status on the front page of any public-sector conversation. The
              deployment pattern depends on whether the agency strictly requires FedRAMP or
              operates a different compliance baseline.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/4">
                      Compliance dimension
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-3/4">
                      Status / approach
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {COMPLIANCE_ROWS.map((row) => (
                    <tr key={row.dimension} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.dimension}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four public-sector scenarios we hear
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These are the recurring shapes of public-sector WebCenter AP conversations in 2026.
              Each maps to a different sequencing and a different compliance posture.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on public-sector modernizations
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement modes, sized to the procurement
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Standard consulting is the default delivery mode for public-sector contract vehicles.
              Forward Deployed Engineering is available where requirements are evolving during
              delivery — for example, transparency reporting integration or per-grant coding being
              designed mid-build.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.tag} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.tag}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Contract vehicle, scope, and pricing are established per situation on a Decision
              Call. We will work through the right structure for your procurement environment.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, with public-sector procurement
              considerations, eight diagnostic questions, and a vendor evaluation checklist that
              maps cleanly onto a public-sector RFP scoring rubric.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on public-sector WebCenter modernization
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your public-sector modernization
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist with direct delivery history inside the US House of
              Representatives, the US Department of Agriculture, and the State of Alaska. Walk
              through your agency's WebCenter implementation, the compliance posture options, and
              the procurement cycle in the context of your specific environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-government-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Public-Sector Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterGovernmentPublicSector;
